import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {
  FONT_PROXIMA_13,
  FONT_PROXIMA_11,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {TooltipPlacement, TooltipWrapper} from 'ui-library/bs/Tooltip';
import {
  BLUE,
  BLUE_LIGHTER,
  BLUE_POWDER,
  GRAY_DARK,
  GRAY_SEMIDARK,
  RED_LIGHT,
  PURPLE,
  PURPLE_LIGHT,
} from 'ui-library/Colors';
import BenchlingDuotoneIcon, {
  BenchlingDuotoneIconName,
  isBenchlingDuotoneIconName,
} from 'ui-library/icons/BenchlingDuotoneIcon';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {BASE_FAILED_SIDEBAR_ITEM_DATA, SIDEBAR_SPINNER_ICON} from './SidebarItemDatas';

/**
 * Returns the appropriate icon style class based on icon type and selection state.
 */
function getIconStyleClass(icon: BenchlingDuotoneIconName | BenchlingIconName, isSelected: boolean): string {
  if (icon === BASE_FAILED_SIDEBAR_ITEM_DATA.icon) {
    return css(sidebarItemStyles.errorIcon);
  }
  if (icon === 'sparkle') {
    return css(sidebarItemStyles.sparkleIcon);
  }
  return css(isSelected ? sidebarItemStyles.selectedMonotoneIcon : sidebarItemStyles.unselectedIcon);
}

/** @package */
export interface SidebarItemProps {
  icon: BenchlingDuotoneIconName | BenchlingIconName;
  isSelected: boolean;
  onSelect: () => void;
  title: ReactNode;
  isFlaggingRows: boolean;
  disabled?: boolean;
  inactive?: boolean;
  tooltipText?: string;
  subtitleText?: string;
  dataTestAttrsKey?: DataTestAttrs['key'];
}

/** @package */
export default function SidebarItem(props: SidebarItemProps): JSX.Element {
  return (
    <TooltipWrapper tooltipPlacement={TooltipPlacement.AUTO} tooltipTitle={props.tooltipText ?? null}>
      <div
        className={css(
          sidebarItemStyles.wrapper,
          props.isSelected && sidebarItemStyles.selectedSidebarItem,
          props.disabled === true && sidebarItemStyles.disabledSidebarItem,
          props.inactive === true && sidebarItemStyles.inactiveSidebarItem
        )}
        onClick={() => {
          if (!props.disabled) {
            props.onSelect();
          }
        }}
        {...getTestProps({component: 'SidebarItem', element: 'root', key: props.dataTestAttrsKey})}
      >
        <div className={css(sidebarItemStyles.leftWrapper)}>
          <div className={css(sidebarItemStyles.iconWrapper)}>
            {props.isSelected && isBenchlingDuotoneIconName(props.icon) ? (
              <BenchlingDuotoneIcon
                icon={props.icon}
                primaryColor={BLUE}
                secondaryColor={BLUE_LIGHTER}
                spin={props.icon === SIDEBAR_SPINNER_ICON}
              />
            ) : (
              <BenchlingIcon
                icon={props.icon}
                className={classNames(
                  props.icon === SIDEBAR_SPINNER_ICON && 'fa-spin',
                  getIconStyleClass(props.icon, props.isSelected)
                )}
              />
            )}
          </div>
          {props.isFlaggingRows && (
            <div className={css(sidebarItemStyles.iconWrapper)}>
              <BenchlingIcon icon="flag" className={css(sidebarItemStyles.flagIcon)} />
            </div>
          )}
          <div className={css(sidebarItemStyles.titleWrapper)}>
            {props.title}
            {props.subtitleText && (
              <div className={css(sidebarItemStyles.subtitleText)}>{props.subtitleText}</div>
            )}
          </div>
        </div>
      </div>
    </TooltipWrapper>
  );
}

/** @package */
export const sidebarItemStyles = StyleSheet.create({
  wrapper: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    marginTop: 3,
    padding: '7px 9px 7px 9px',
    borderRadius: 2,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    ':hover': {backgroundColor: BLUE_POWDER},
  },
  leftWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    wordBreak: 'break-word',
    width: '100%',
  },
  iconWrapper: {
    flexShrink: 0,
    marginRight: 8,
  },
  titleWrapper: {
    flexDirection: 'column',
    flexGrow: 1,
  },
  selectedSidebarItem: {
    backgroundColor: BLUE_POWDER,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  selectedMonotoneIcon: {
    color: BLUE,
  },
  unselectedIcon: {
    color: GRAY_DARK,
  },
  errorIcon: {
    color: RED_LIGHT,
  },
  disabledSidebarItem: {
    color: GRAY_DARK,
    cursor: 'default',
  },
  inactiveSidebarItem: {
    color: GRAY_DARK,
    fontStyle: 'italic',
  },
  subtitleText: {
    ...FONT_PROXIMA_11,
    color: GRAY_SEMIDARK,
  },
  flagIcon: {
    color: PURPLE,
  },
  sparkleIcon: {
    color: PURPLE_LIGHT,
  },
});
